import { IOneDriveRootDetails } from "../../../src/taskpane/interfaces/IOneDriveRootDetails";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { currentSharepointFolderState } from "../../../src/taskpane/redux/models/reduxModels";
import type { CurrentFolderPermissionState } from "./currentFolderPermissionState";

export interface DirectoryPermissionIdentity {
  key: string;
  isComplete: boolean;
  isOneDrive: boolean;
  rootFolder: boolean;
  sharePointListId?: string;
  sharePointUniqueId?: string;
  webUrl?: string;
}

export const normalizeSharePointId = (value?: string): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  return value.replace("{", "").replace("}", "").trim().toLowerCase() || undefined;
};

const normalizeWebUrl = (value?: string): string | undefined =>
  value?.trim().replace(/\/+$/, "").toLowerCase() || undefined;

const normalizeDirectoryPath = (value?: string): string | undefined =>
  value?.trim().replace(/\\/g, "/").replace(/\/+/g, "/").replace(/\/+$/, "").toLowerCase() || undefined;

const normalizeRequestSegment = (value?: string): string | undefined =>
  value?.trim().toLowerCase() || undefined;

const savingDirectoryType = ComponentTypes.Saving;

export const isOneDriveDirectory = (currentDirectory?: IPageSettings): boolean =>
  currentDirectory?.type === ComponentTypes.OneDrive || currentDirectory?.type === ComponentTypes.OneDriveSubFolder;

export const isOneDriveRootDirectory = (currentDirectory?: IPageSettings): boolean =>
  currentDirectory?.type === ComponentTypes.OneDrive && !currentDirectory?.parentId;

export const getOneDriveRootSharePointUniqueId = ({
  currentDirectory,
  oneDriveRootDetails,
}: {
  currentDirectory?: IPageSettings;
  oneDriveRootDetails?: IOneDriveRootDetails;
}): string | undefined =>
  isOneDriveRootDirectory(currentDirectory)
    ? currentDirectory?.sharePointUniqueId ?? oneDriveRootDetails?.sharePointItemId
    : undefined;

export const getEffectivePermissionDirectory = ({
  currentDirectory,
  workingDirectories,
}: {
  currentDirectory?: IPageSettings;
  workingDirectories?: IPageSettings[];
}): IPageSettings | undefined =>
  currentDirectory?.type === savingDirectoryType
    ? workingDirectories?.filter(directory => directory.type !== savingDirectoryType).pop() ?? currentDirectory
    : currentDirectory;

export const isTeamsChannelLibraryRoot = (currentDirectory?: IPageSettings): boolean =>
  currentDirectory?.isTeamsRootFolder === true
  && (currentDirectory?.isPrivateTeamsChannel === true || currentDirectory?.isSharedTeamsChannel === true);

const isSharePointLibraryRootDirectory = (currentDirectory?: IPageSettings): boolean => {
  const normalizedDirectory = normalizeDirectoryPath(currentDirectory?.directory);

  return currentDirectory?.type === ComponentTypes.SharePointDocuments
    || (
      currentDirectory?.type === ComponentTypes.SharePointFolders
      && currentDirectory?.isTeamsRootFolder !== true
      && Boolean(currentDirectory?.sharePointListId)
      && Boolean(normalizedDirectory)
      && !normalizedDirectory.includes("/")
    );
};

const isRootDirectory = (currentDirectory?: IPageSettings, isOneDrive?: boolean): boolean =>
  !currentDirectory?.parentId
  || isSharePointLibraryRootDirectory(currentDirectory)
  || isTeamsChannelLibraryRoot(currentDirectory)
  || currentDirectory?.type === ComponentTypes.SharePointList
  || (Boolean(isOneDrive) && !currentDirectory?.directory);

export const isPermissionRootDirectory = (currentDirectory?: IPageSettings): boolean =>
  Boolean(currentDirectory) && isRootDirectory(currentDirectory, isOneDriveDirectory(currentDirectory));

export const getDirectoryRequestKey = ({
  currentDirectory,
  oneDriveRootDetails,
}: {
  currentDirectory?: IPageSettings;
  oneDriveRootDetails?: IOneDriveRootDetails;
}): string | undefined => {
  if (!currentDirectory) {
    return undefined;
  }

  const isOneDrive = isOneDriveDirectory(currentDirectory);
  const webUrl = normalizeWebUrl(isOneDrive ? oneDriveRootDetails?.sharePointSiteUrl ?? currentDirectory?.webUrl : currentDirectory?.webUrl);
  const sharePointListId = normalizeSharePointId(currentDirectory?.sharePointListId ?? (isOneDrive ? oneDriveRootDetails?.sharePointListId : undefined));
  const rootFolder = isRootDirectory(currentDirectory, isOneDrive);
  const directoryItemKey = currentDirectory?.parentId && currentDirectory?.id
    ? `${normalizeRequestSegment(currentDirectory.parentId) ?? "unknown"}|${normalizeRequestSegment(currentDirectory.id) ?? "unknown"}`
    : undefined;
  const directoryPathKey = normalizeDirectoryPath(currentDirectory?.serverRelativePath ?? currentDirectory?.directory);

  const requestScope = rootFolder
    ? "root"
    : directoryItemKey
      ? `item|${directoryItemKey}`
      : directoryPathKey
        ? `path|${directoryPathKey}`
        : "pending";

  return `${isOneDrive ? "onedrive" : "sharepoint"}|${webUrl ?? "unknown"}|${sharePointListId ?? "unknown"}|${requestScope}`;
};

export const getDirectoryPermissionIdentity = ({
  currentDirectory,
  oneDriveRootDetails,
  resolvedSharePointUniqueId,
}: {
  currentDirectory?: IPageSettings;
  oneDriveRootDetails?: IOneDriveRootDetails;
  resolvedSharePointUniqueId?: string;
}): DirectoryPermissionIdentity => {
  const isOneDrive = isOneDriveDirectory(currentDirectory);
  const webUrl = normalizeWebUrl(isOneDrive ? oneDriveRootDetails?.sharePointSiteUrl ?? currentDirectory?.webUrl : currentDirectory?.webUrl);
  const sharePointListId = normalizeSharePointId(currentDirectory?.sharePointListId ?? (isOneDrive ? oneDriveRootDetails?.sharePointListId : undefined));
  const sharePointUniqueId = normalizeSharePointId(resolvedSharePointUniqueId ?? currentDirectory?.sharePointUniqueId);
  const rootFolder = isRootDirectory(currentDirectory, isOneDrive);

  const isComplete = Boolean(webUrl && sharePointListId && (rootFolder || sharePointUniqueId));
  const identityScope = rootFolder ? "root" : sharePointUniqueId ?? "pending";

  return {
    key: `${isOneDrive ? "onedrive" : "sharepoint"}|${webUrl ?? "unknown"}|${sharePointListId ?? "unknown"}|${identityScope}`,
    isComplete,
    isOneDrive,
    rootFolder,
    sharePointListId,
    sharePointUniqueId,
    webUrl,
  };
};

export const getResolvedSharePointUniqueIdForDirectoryRequest = ({
  directoryRequestKey,
  permissionState,
}: {
  directoryRequestKey?: string;
  permissionState?: CurrentFolderPermissionState;
}): string | undefined =>
  permissionState?.directoryRequestKey === directoryRequestKey
    ? permissionState.resolvedSharePointUniqueId
    : undefined;

export const getPermissionScopedUniqueId = ({
  currentDirectory,
  sharePointUniqueId,
}: {
  currentDirectory?: IPageSettings;
  sharePointUniqueId?: string;
}): string | undefined =>
  isPermissionRootDirectory(currentDirectory)
    ? undefined
    : sharePointUniqueId;

export const isCurrentSharepointFolderForIdentity = ({
  currentSharepointFolder,
  identityKey,
}: {
  currentSharepointFolder?: currentSharepointFolderState;
  identityKey?: string;
}): boolean => Boolean(identityKey && currentSharepointFolder?.permissionIdentityKey === identityKey);
